/**
 * URL Configuration and Sanitization Utilities
 * 
 * This module provides utilities to ensure URLs are correctly configured
 * for production environments, preventing internal/preview URLs from being exposed.
 */

// Production domain - the canonical domain for EvalentX
const PRODUCTION_DOMAIN = 'https://evalentx.com';

// Patterns that indicate internal/preview URLs that should not be used in production
const BLOCKED_URL_PATTERNS = [
  'emergent.host',
  'preview.emergentagent.com',
  'localhost',
  '127.0.0.1',
  '.local',
  'ngrok.io'
];

/**
 * Check if a URL is an internal/preview URL that shouldn't be used publicly
 */
export const isInternalUrl = (url) => {
  if (!url) return false;
  const urlLower = url.toLowerCase();
  return BLOCKED_URL_PATTERNS.some(pattern => urlLower.includes(pattern));
};

/**
 * Get the correct frontend URL for redirects and sharing
 * Falls back to production domain if internal URL is detected
 */
export const getFrontendUrl = () => {
  // First try window.location.origin (works in browser)
  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin;
    
    // If it's an internal URL, use production domain
    if (isInternalUrl(origin)) {
      console.warn(`[URL Config] Blocked internal URL: ${origin}, using production domain`);
      return PRODUCTION_DOMAIN;
    }
    
    return origin;
  }
  
  // Fallback to production domain
  return PRODUCTION_DOMAIN;
};

/**
 * Get the API base URL
 * This should point to the backend API
 */
export const getApiUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  // If backend URL is not set or is internal, construct from frontend URL
  if (!backendUrl || isInternalUrl(backendUrl)) {
    const frontendUrl = getFrontendUrl();
    // In production, API is typically on the same domain
    return frontendUrl;
  }
  
  return backendUrl;
};

/**
 * Sanitize a URL for public use (sharing, redirects, etc.)
 * Replaces internal domains with production domain
 */
export const sanitizePublicUrl = (url) => {
  if (!url) return url;
  
  // Check each blocked pattern and replace with production domain
  for (const pattern of BLOCKED_URL_PATTERNS) {
    if (url.toLowerCase().includes(pattern)) {
      // Extract the path from the URL
      try {
        const urlObj = new URL(url);
        return `${PRODUCTION_DOMAIN}${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
      } catch (e) {
        // If URL parsing fails, just return production domain
        console.warn(`[URL Config] Failed to parse URL: ${url}`);
        return PRODUCTION_DOMAIN;
      }
    }
  }
  
  return url;
};

/**
 * Build a full URL for sharing or redirects
 */
export const buildPublicUrl = (path) => {
  const baseUrl = getFrontendUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Build an auth URL that preserves the current page so the user is
 * returned to the exact intended destination after login/registration.
 *
 * Example:
 *   loginUrlWithRedirect('/codearena/auth')
 *   -> '/codearena/auth?redirect=%2Fcodearena%2Fmock-interview%2Fabc'
 *
 * Falls back to the auth path alone when called outside the browser.
 */
export const loginUrlWithRedirect = (authPath = '/codearena/auth', overridePath = null) => {
  // If we are already on the auth path (e.g. StrictMode dev re-run after a
  // first redirect already happened), keep the current URL intact so we
  // don't strip out the redirect query the caller just set.
  if (!overridePath && typeof window !== 'undefined' && window.location?.pathname === authPath) {
    return `${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`;
  }

  let target = overridePath;
  if (!target && typeof window !== 'undefined') {
    target = `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`;
  }
  if (!target || target === authPath || target.startsWith(`${authPath}?`) || target.startsWith(`${authPath}/`)) {
    return authPath;
  }
  // Only allow same-origin paths
  if (!target.startsWith('/') || target.startsWith('//')) {
    return authPath;
  }
  return `${authPath}?redirect=${encodeURIComponent(target)}`;
};

export default {
  PRODUCTION_DOMAIN,
  isInternalUrl,
  getFrontendUrl,
  getApiUrl,
  sanitizePublicUrl,
  buildPublicUrl,
  loginUrlWithRedirect
};
